/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, 
  Eye,
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Camera, 
  FileUp, 
  Save, 
  Send, 
  X,
  Info,
  ChevronRight,
  FlaskConical,
  Beaker,
  History,
  Scan,
  Maximize2
} from 'lucide-react';

interface SterilityObservationRecordingProps {
  onBack: () => void;
  onSave: () => void;
}

const SterilityObservationRecording: React.FC<SterilityObservationRecordingProps> = ({ onBack, onSave }) => {
  // Form State
  const [visualClarity, setVisualClarity] = useState<string>('');
  const [indicators, setIndicators] = useState({
    colorChange: false,
    gasFormation: false,
    visibleParticles: false,
    surfaceFilm: false,
    sedimentFormation: false
  });
  const [remarks, setRemarks] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  
  // Computed Result State
  const [result, setResult] = useState<'PASS' | 'FAIL' | 'PENDING'>('PENDING');
  const [isAbnormal, setIsAbnormal] = useState(false);

  // Constants
  const testData = {
    testId: 'ST-2024-X45',
    productName: 'Insulin Glargine',
    productBatch: 'B240501',
    testMethod: 'Membrane Filtration',
    mediaType: 'FTM (Fluid Thioglycollate Medium)',
    mediaBatch: 'MT-00921',
    lotNumber: 'LOT-2024-X45',
    tubeId: 'T-FTM-01',
    observationDay: 'Day 7',
    status: 'Observation Due'
  };

  const timeline = [
    { day: 'Day 3', status: 'Completed', user: 'P Shyam Kumar', date: '15-Aug 09:30' },
    { day: 'Day 7', status: 'Current', user: '-', date: '-' },
    { day: 'Day 14', status: 'Pending', user: '-', date: '26-Aug-2024 (Scheduled)' }
  ];

  // Logic to calculate result
  useEffect(() => {
    const hasAbnormality = 
      visualClarity === 'Growth Observed' || 
      visualClarity === 'Turbid' ||
      indicators.colorChange || 
      indicators.gasFormation || 
      indicators.visibleParticles || 
      indicators.surfaceFilm || 
      indicators.sedimentFormation;

    setIsAbnormal(hasAbnormality);

    if (visualClarity === '') {
      setResult('PENDING');
    } else if (visualClarity === 'Growth Observed' || hasAbnormality) {
      setResult('FAIL');
    } else {
      setResult('PASS');
    }
  }, [visualClarity, indicators]);

  const toggleIndicator = (key: keyof typeof indicators) => {
    setIndicators(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'Observation Due': return 'bg-blue-50 text-blue-600 border-blue-100 ring-4 ring-blue-50';
      default: return 'bg-slate-50 text-slate-500 border-slate-100';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/30 flex flex-col pt-4 media-prep-scope">
      {/* Header Sticky Area */}
      <header className="bg-white border-b border-slate-200 px-8 py-6 sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <button 
              onClick={onBack}
              className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-100 transition-all shadow-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-black text-[#0F172A] tracking-tight">{testData.testId}</h1>
                <span className={`px-3 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusBadge(testData.status)}`}>
                  {testData.status}
                </span>
                <span className="px-2 py-0.5 bg-orange-500 text-white text-[9px] font-black rounded uppercase tracking-widest">
                  {testData.observationDay} Checkpoint
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-slate-500 font-bold text-[11px] uppercase">
                <div className="flex items-center gap-1.5"><span className="text-slate-400">PRODUCT:</span> {testData.productName} ({testData.productBatch})</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-400">METHOD:</span> {testData.testMethod}</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-400">TUBE:</span> {testData.tubeId}</div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 max-w-sm">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-1 flex items-center gap-2 group focus-within:ring-4 focus-within:ring-orange-500/10 focus-within:border-orange-500 transition-all">
              <div className="flex items-center gap-2 pl-3 py-2 border-r border-slate-200 pr-3">
                <Scan className="w-4 h-4 text-orange-500" />
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">Scan Tube</span>
              </div>
              <div className="flex-1 relative flex items-center">
                <input 
                  type="text" 
                  placeholder="Enter or Scan Tube ID..."
                  className="w-full bg-transparent border-none text-xs font-bold text-slate-700 outline-none placeholder:text-slate-300 px-2"
                />
                <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors">
                   <Maximize2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div className="hidden xl:grid grid-cols-2 gap-x-8 gap-y-2 px-6 py-2 border-l border-slate-100">
             <div className="flex flex-col">
                <span className="text-[9px] font-black text-slate-400 uppercase">Media Batch</span>
                <span className="text-xs font-bold text-slate-700">{testData.mediaBatch}</span>
             </div>
             <div className="flex flex-col">
                <span className="text-[9px] font-black text-slate-400 uppercase">System Lot</span>
                <span className="text-xs font-bold text-slate-700">{testData.lotNumber}</span>
             </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-[1600px] mx-auto w-full p-8 grid grid-cols-1 xl:grid-cols-12 gap-8 pb-32">
        
        {/* Left Panel - Timeline */}
        <div className="xl:col-span-3 space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-200/60 p-6 shadow-sm sticky top-32">
            <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest mb-8 flex items-center gap-2">
              <History className="w-4 h-4 text-orange-500" />
              Observation Timeline
            </h3>
            
            <div className="space-y-10 relative before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-100">
              {timeline.map((step, idx) => (
                <div key={idx} className="relative pl-10">
                  <div className={`absolute left-0 top-1.5 w-8 h-8 rounded-full border-4 border-white shadow-md flex items-center justify-center z-10 transition-all ${
                    step.status === 'Completed' ? 'bg-emerald-500 text-white' :
                    step.status === 'Current' ? 'bg-white text-blue-600 ring-4 ring-blue-50' :
                    'bg-slate-100 text-slate-400'
                  }`}>
                    {step.status === 'Completed' ? <CheckCircle2 className="w-4 h-4" /> : <div className={`w-2 h-2 rounded-full ${step.status === 'Current' ? 'bg-blue-600 animate-pulse' : 'bg-slate-300'}`} />}
                  </div>
                  <div className="space-y-1">
                    <p className={`text-[11px] font-black uppercase tracking-widest ${
                      step.status === 'Completed' ? 'text-slate-800' :
                      step.status === 'Current' ? 'text-blue-700' :
                      'text-slate-400'
                    }`}>{step.day}</p>
                    <p className="text-xs font-medium text-slate-500">{step.status === 'Current' ? 'In Progress' : step.status}</p>
                    {step.status === 'Completed' && (
                      <div className="pt-2">
                         <p className="text-[10px] font-black text-slate-500 uppercase">{step.user}</p>
                         <p className="text-[10px] font-bold text-slate-500 italic">{step.date}</p>
                      </div>
                    )}
                    {step.status === 'Pending' && (
                      <p className="text-[10px] font-bold text-slate-500 mt-1">{step.date}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Center Panel - Main Recording Form */}
        <div className="xl:col-span-6 space-y-6">
          {/* Alert Banner */}
          <AnimatePresence>
            {isAbnormal && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-red-50 border border-red-200 rounded-2xl p-5 flex gap-4 items-start shadow-sm"
              >
                <div className="bg-red-100 p-2 rounded-xl text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                   <h4 className="text-sm font-black text-red-800 uppercase tracking-tight">Contamination detected or suspected</h4>
                   <p className="text-xs font-bold text-red-600/80 mt-1 leading-relaxed">
                     The recorded observations indicate potential microbial growth. A deviation investigation may be required as per SOP-QC-088. Notify QA Supervisor immediately.
                   </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Section 1: Visual Clarity */}
          <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-8 shadow-sm space-y-6">
            <div className="flex items-center gap-3 border-b border-slate-50 pb-4">
               <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Eye className="w-5 h-5" />
               </div>
               <div>
                  <h3 className="text-sm font-black text-[#0F172A] tracking-tight">SECTION 1: VISUAL CLARITY ASSESSMENT</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Mandatory GMP Recording</p>
               </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-black text-slate-800 uppercase tracking-widest">Visual Clarity</label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {['Clear', 'Slight Turbidity', 'Turbid', 'Growth Observed'].map((option) => (
                  <button
                    key={option}
                    onClick={() => setVisualClarity(option)}
                    className={`px-4 py-3 rounded-2xl text-[11px] font-black uppercase tracking-tight transition-all border-2 text-center
                      ${visualClarity === option 
                        ? 'bg-[#0F172A] text-white border-[#0F172A] shadow-lg scale-[1.02]' 
                        : 'bg-slate-50 text-slate-400 border-transparent hover:bg-slate-100 hover:text-slate-600'
                      }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2: Contamination Indicators */}
          <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-8 shadow-sm space-y-8">
            <div className="flex items-center gap-3 border-b border-slate-50 pb-4">
               <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <FlaskConical className="w-5 h-5" />
               </div>
               <div>
                  <h3 className="text-sm font-black text-[#0F172A] tracking-tight">SECTION 2: CONTAMINATION INDICATORS</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Specific growth identifiers</p>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { key: 'colorChange', label: 'Color Change' },
                { key: 'gasFormation', label: 'Gas Formation' },
                { key: 'visibleParticles', label: 'Visible Particles' },
                { key: 'surfaceFilm', label: 'Surface Film' },
                { key: 'sedimentFormation', label: 'Sediment Formation' }
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                  <span className="text-[11px] font-black text-slate-700 uppercase tracking-widest">{item.label}</span>
                  <div className="flex bg-white rounded-xl p-1 shadow-sm border border-slate-100">
                    <button 
                      onClick={() => setIndicators(prev => ({ ...prev, [item.key]: true }))}
                      className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${indicators[item.key as keyof typeof indicators] ? 'bg-red-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      Yes
                    </button>
                    <button 
                      onClick={() => setIndicators(prev => ({ ...prev, [item.key]: false }))}
                      className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${!indicators[item.key as keyof typeof indicators] ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Technician Remarks */}
          <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-8 shadow-sm space-y-6 text-black">
            <div className="flex items-center gap-3 border-b border-slate-50 pb-4">
               <div className="w-10 h-10 rounded-2xl bg-slate-50 text-slate-600 flex items-center justify-center">
                  <Beaker className="w-5 h-5" />
               </div>
               <div>
                  <h3 className="text-sm font-black text-[#0F172A] tracking-tight">SECTION 3: TECHNICIAN REMARKS</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Detail morphology and observations</p>
               </div>
            </div>

            <div className="space-y-4">
               <textarea 
                 value={remarks}
                 onChange={(e) => setRemarks(e.target.value)}
                 className={`w-full min-h-[160px] p-6 bg-slate-50 border border-slate-100 rounded-3xl text-sm font-medium focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all placeholder:text-slate-400 ${isAbnormal && remarks === '' ? 'ring-2 ring-red-100 border-red-200' : ''}`}
                 placeholder="Enter detailed observations, abnormalities, morphology, or contamination notes."
               />
               {isAbnormal && remarks === '' && (
                 <p className="text-[10px] font-black text-red-500 uppercase flex items-center gap-1.5 px-1">
                   <AlertTriangle className="w-3 h-3" />
                   Remarks are mandatory when abnormal observations exist
                 </p>
               )}
            </div>
          </div>

          {/* Section 4: Attachments */}
          <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-8 shadow-sm space-y-6">
             <div className="flex items-center justify-between border-b border-slate-50 pb-4">
                <div className="flex items-center gap-3">
                   <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center">
                      <Camera className="w-5 h-5" />
                   </div>
                   <div>
                      <h3 className="text-sm font-black text-[#0F172A] tracking-tight">SECTION 4: ATTACHMENTS</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Photos & Evidence</p>
                   </div>
                </div>
                <div className="flex gap-2">
                   <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 text-[10px] font-black uppercase rounded-xl hover:bg-slate-200 transition-all">
                      <Camera className="w-4 h-4" />
                      Take Photo
                   </button>
                   <button className="flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-[10px] font-black uppercase rounded-xl hover:scale-105 transition-all shadow-lg shadow-slate-900/10">
                      <FileUp className="w-4 h-4" />
                      Upload File
                   </button>
                </div>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center py-8 border-2 border-dashed border-slate-100 rounded-3xl col-span-full">
                  No files uploaded yet. Optional evidence for Pass, recommended for Growth.
                </p>
             </div>
          </div>
        </div>

        {/* Right Panel - Result Preview */}
        <div className="xl:col-span-3 space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-200/60 p-8 shadow-xl shadow-slate-200/20 sticky top-32">
             <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest mb-6 underline decoration-orange-500 decoration-2 underline-offset-8">Result Preview Panel</h3>
             
             <div className="space-y-8">
                <div className="flex flex-col items-center justify-center py-8 bg-slate-50/50 rounded-3xl border border-slate-100">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Current Disposition</p>
                   {result === 'PENDING' ? (
                     <div className="flex flex-col items-center gap-2">
                        <Clock className="w-12 h-12 text-slate-200 animate-pulse" />
                        <span className="text-lg font-black text-slate-300">AWAITING INPUT</span>
                     </div>
                   ) : result === 'PASS' ? (
                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shadow-lg shadow-emerald-500/10">
                           <CheckCircle2 className="w-10 h-10" />
                        </div>
                        <span className="text-2xl font-black text-emerald-600">PASS</span>
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 rounded-full">
                           <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                           <span className="text-[9px] font-black text-emerald-600 uppercase">Compliant</span>
                        </div>
                     </div>
                   ) : (
                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 shadow-lg shadow-rose-500/10 animate-bounce">
                           <AlertTriangle className="w-10 h-10" />
                        </div>
                        <span className="text-2xl font-black text-rose-600">FAIL</span>
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-red-50 rounded-full">
                           <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                           <span className="text-[9px] font-black text-red-600 uppercase">Non-Compliant</span>
                        </div>
                     </div>
                   )}
                </div>

                <div className="space-y-4 px-2">
                   <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                      <span className="text-slate-400">Timestamp</span>
                      <span className="text-slate-800">2-JUN-2026 10:15</span>
                   </div>
                   <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                      <span className="text-slate-400">Operator ID</span>
                      <span className="text-slate-800">TECH-8821</span>
                   </div>
                   <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                      <span className="text-slate-400">Location</span>
                      <span className="text-slate-800">MICRO-LAB-02</span>
                   </div>
                </div>

                <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50 flex gap-3 items-start">
                   <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                   <p className="text-[10px] font-bold text-blue-700 leading-tight">
                     This result is automatically calculated but must be verified by the technician before final submission.
                   </p>
                </div>
             </div>
          </div>
        </div>
      </main>

      {/* Bottom Action Bar */}
        <div className="w-[90%] mx-4 flex items-center justify-between">
           <button 
             onClick={onBack}
             className="px-6 py-3 bg-white border border-slate-200 text-slate-500 text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-slate-50 transition-all"
           >
             Cancel
           </button>
           
           <div className="flex items-center gap-4">
              <button 
                onClick={onSave}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-[#0F172A] text-[#0F172A] text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-slate-50 transition-all"
              >
                <Save className="w-4 h-4" />
                Save Draft
              </button>
              <button 
                onClick={onSave}
                disabled={visualClarity === '' || (isAbnormal && remarks === '')}
                className={`flex items-center gap-2 px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl
                  ${(visualClarity === '' || (isAbnormal && remarks === ''))
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200 shadow-none'
                    : 'bg-orange-600 text-white hover:scale-105 active:scale-95 shadow-orange-600/20'
                  }`}
              >
                <Send className="w-4 h-4" />
                Submit Observation
              </button>
           </div>
        </div>
    </div>
  );
};

export default SterilityObservationRecording;
